import {
  createSearchId,
  SearchQuery,
  SubscriptionTypeFilter,
  useSearch,
  useSearchContexts,
} from '@ilteducation/content-search';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { storeToRefs } from 'pinia';
import useSubscriptions from '@/foundations/helpers/use-subscriptions';
import { Ref, watch, computed } from 'vue';

export const useMaterialsForVideoSearch = (
  videoId: Ref<string>,
  refDataIds: { categoryId: Ref<string>; areaId: Ref<string> },
) => {
  const searchStore = useSearch<MaterialV1>('Material');
  const { result: resultVideo, isLoading: isLoadingVideo, hasMore: hasMoreVideo } = storeToRefs(searchStore);
  const { search, next: nextVideo } = searchStore;

  const subscriptions = useSubscriptions();
  const subscriptionTypes = subscriptions.value.map((s) => s.type);

  const searchMaterials = () => {
    search(
      '',
      {
        ...SubscriptionTypeFilter(subscriptionTypes),
        'contentLinks.id': videoId.value,
      },
      20,
      [],
      'and',
    );
  };

  const refdataContextSearch = useSearchContexts<MaterialV1>('Material');
  const {
    result: resultCategory,
    isLoading: isLoadingCategory,
    hasMore: hasMoreCategory,
  } = storeToRefs(refdataContextSearch);
  const searchQuery = computed<SearchQuery>(() => {
    return {
      q: '',
      filter: {
        ...SubscriptionTypeFilter(subscriptionTypes),
        'refdataLinks.id': [refDataIds.categoryId.value, refDataIds.areaId.value],
      },
      sort: [],
      groupFilterMode: 'or',
    };
  });

  const searchByCategoryId = computed(() => createSearchId('Material', searchQuery.value));

  watch(
    videoId,
    () => {
      searchMaterials();
    },
    { immediate: true },
  );

  watch(
    [refDataIds.categoryId, refDataIds.areaId],
    () => {
      refdataContextSearch.search(searchQuery.value, 20);
    },
    { immediate: true },
  );

  const result = computed(() => [...resultVideo.value, ...resultCategory.value(searchByCategoryId.value)]);
  const isLoading = computed(() => isLoadingVideo.value || isLoadingCategory.value(searchByCategoryId.value));
  const hasMore = computed(() => hasMoreVideo.value || hasMoreCategory.value(searchByCategoryId.value));

  const next = () => {
    if (hasMoreVideo.value) {
      nextVideo();
    }
    if (hasMoreCategory.value(searchByCategoryId.value)) {
      refdataContextSearch.next(searchByCategoryId.value);
    }
  };

  return {
    result,
    isLoading,
    hasMore,
    next,
    searchMaterials,
  };
};
